// client/src/components/influencer/project/modals/ProjectDetailModal.js
import React, { useMemo, useState, useEffect } from 'react';
import ProjectApplyModal from './ProjectApplyModal';
import ProjectApplyConfirmModal from './ProjectApplyConfirmModal';
import ProjectJourneyBox from '../../../common/ProjectJourneyBox';
import CommonGuideBox from '../../../common/CommonGuideBox';
import '../../../../styles/marketer/modals/ProjectPreviewModal.css';
import { linkifyHtml } from '../../../../utils/linkify';

function toDateStr(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
}

export default function ProjectDetailModal({ open, onClose, project, keepMounted, onApplySuccess }) {
  const [localProject, setLocalProject] = useState(project);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  
  // project prop이 변경되면 localProject도 업데이트
  useEffect(() => {
    setLocalProject(project);
  }, [project]);
  
  const v = localProject || null;

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 전체 이미지 배열
  const allImages = useMemo(() => {
    if (!v) return [];
    return [
      v.cover,
      ...(Array.isArray(v.gallery) ? v.gallery : [])
    ].filter(Boolean);
  }, [v]);

  // 이미지 인덱스 초기화
  useEffect(() => {
    if (open) {
      setCurrentImageIndex(0);
    }
  }, [open, v]);

  // 이미지 프리로딩
  useEffect(() => {
    if (allImages.length === 0) return;
    
    allImages.forEach((url) => {
      if (url) {
        const img = new Image();
        img.src = url;
      }
    });
  }, [allImages]);

  // 이미지 로딩 감지
  useEffect(() => {
    if (allImages.length === 0 || currentImageIndex >= allImages.length) return;
    
    setImageLoading(true);
    const img = new Image();
    img.src = allImages[currentImageIndex];
    img.onload = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
  }, [currentImageIndex, allImages]);

  // 모달이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (open) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll'; // 스크롤바 자리 유지
      
      return () => {
        // 모달이 닫힐 때 원래대로 복원
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflowY = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  const handlePrevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (imageLoading) return;
    const newIndex = currentImageIndex === 0 ? allImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
  };

  const handleNextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (imageLoading) return;
    const newIndex = currentImageIndex === allImages.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
  };

  // 지원 완료 핸들러 - 상태 즉시 업데이트
  const handleApplySuccess = () => {
    setShowApplyModal(false);
    setShowConfirmModal(true);
    
    // 로컬 상태 즉시 업데이트
    setLocalProject(prev => ({
      ...prev,
      isApplied: true,
      appliedCount: (prev?.appliedCount || 0) + 1
    }));
    
    // 상위 컴포넌트에도 알림
    onApplySuccess?.(v?.id);
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    onClose();
  };

  const shouldUnmount = !keepMounted && !open;
  if (shouldUnmount) return null;
  if (!open) return null;

  // 제품명 추출
  const getProductName = () => {
    if (!v) return '광고 대상';
    return v.productName || v.product?.name || v.product?.placeName || v.product?.customTitle || '광고 대상';
  };

  // 광고 형식 추출
  const getServiceTypeLabel = () => {
    if (v?.serviceType === 'delivery') return '제품 제공';
    if (v?.serviceType === 'purchase') return '제품 미제공';
    if (v?.serviceType === 'visit') return '장소 방문';
    if (v?.serviceType === 'free' || v?.serviceType === 'custom') return '자율';
    return '';
  };

  // 영어 병기용 서비스 타입 표기 — 렌더 전용 상수 (FEATURE_REVIEWER_UI_EN_BILINGUAL 샘플 7)
  const SERVICE_EN = { delivery: 'product-provided', purchase: 'self-purchase', visit: 'store-visit', free: 'flexible', custom: 'flexible' };

  // 서비스 타입별 안내 문구
  const getServiceTypeDescription = () => {
    if (v?.serviceType === 'delivery') {
      return '해당 프로젝트는 광고주가 리뷰할 제품을 제공해주는 프로젝트 입니다. 프로필에 설정된 주소로 제품이 배송됩니다. (The advertiser provides the product for review — it is shipped to the address in your profile.)';
    }
    if (v?.serviceType === 'purchase') {
      return '해당 프로젝트는 인플루언서가 리뷰할 제품을 직접 구매해야하는 프로젝트입니다, 제품은 제공되지 않습니다. (You purchase the product yourself — it is not provided.)';
    }
    if (v?.serviceType === 'visit') {
      return '해당 프로젝트는 장소/서비스를 홍보하는 프로젝트 입니다. (This project promotes a place or service.)';
    }
    if (v?.serviceType === 'free' || v?.serviceType === 'custom') {
      return '해당 프로젝트는 자율형식으로 진행됩니다. 광고주가 제시하는 상세 가이드를 잘 확인하세요 (Flexible format — follow the advertiser\'s detailed guide.)';
    }
    return '';
  };

  return (
    <>
      <div className="project-preview-modal" style={{ zIndex: 9999 }}>
        <div className="project-preview-modal__content" role="dialog" aria-modal="true">
          {/* Header */}
          <div className="project-preview-modal__header-bar">
            <div className="project-preview-modal__header-title">
              <span>프로젝트 상세보기 (Project Details)</span>
            </div>
            <button onClick={onClose} aria-label="close" className="project-preview-modal__close">×</button>
          </div>

          <div className="project-preview-modal__body">
            
            {/* 프로젝트 소개 문구 */}
            <div className="project-preview-modal__intro">
              <strong>{getProductName()}</strong>을(를) 홍보하는 <strong>{getServiceTypeLabel()}</strong> 프로젝트입니다.
              <span style={{ color: '#6b7686', fontWeight: 500 }}>
                {' '}(A {SERVICE_EN[v?.serviceType] || 'general'} campaign promoting {getProductName()})
              </span>
            </div>

            {/* 서비스 타입별 안내 */}
            <div className="project-preview-modal__service-notice">
              {getServiceTypeDescription()}
            </div>

            {/* 프로젝트 진행 순서 */}
            <ProjectJourneyBox />

            {/* 공통 가이드 */}
            <CommonGuideBox />

            {/* 섹션 2: 상세 정보 그리드 */}
            <div className="project-preview-modal__main-grid">
              
              {/* 왼쪽 컬럼: 프로젝트 및 미션 설명 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                
                {/* 프로젝트 기본 정보 */}
                <div className="project-preview-modal__card">
                  <div className="project-preview-modal__card-title-box">
                    <div className="project-preview-modal__card-title">📦 프로젝트 대상 정보 (Product Info)</div>
                  </div>

                  {/* 이미지 슬라이더 */}
                  {allImages.length > 0 && (
                    <div className="project-preview-modal__image-slider">
                      <div className="project-preview-modal__image-container">
                        <img 
                          src={allImages[currentImageIndex]} 
                          alt={`프로젝트 이미지 ${currentImageIndex + 1}`}
                          className="project-preview-modal__slider-image"
                          style={{ 
                            opacity: imageLoading ? 0.5 : 1,
                            transition: 'opacity 0.2s ease-in-out'
                          }}
                        />
                        {allImages.length > 1 && (
                          <>
                            <button 
                              className="project-preview-modal__slider-btn project-preview-modal__slider-btn--prev"
                              onClick={handlePrevImage}
                              disabled={imageLoading}
                              aria-label="이전 이미지"
                              type="button"
                            >
                              ‹
                            </button>
                            <button 
                              className="project-preview-modal__slider-btn project-preview-modal__slider-btn--next"
                              onClick={handleNextImage}
                              disabled={imageLoading}
                              aria-label="다음 이미지"
                              type="button"
                            >
                              ›
                            </button>
                          </>
                        )}
                      </div>
                      {allImages.length > 1 && (
                        <div className="project-preview-modal__slider-indicator">
                          {currentImageIndex + 1} / {allImages.length}
                        </div>
                      )}
                    </div>
                  )}

                <div className="project-preview-modal__info-list">
                  {v?.serviceType === 'delivery' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🎁</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">광고할 제품 (Product)</div>
                          <div className="project-preview-modal__info-value">{v.product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 판매처의 제품 가격 (Retail price)</div>
                          <div className="project-preview-modal__info-value">{Number(v.product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 참고 URL (Reference URL)</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#3b82f6', wordBreak: 'break-all' }}>{v.product?.url || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {v?.serviceType === 'purchase' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🛒</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">리뷰할 제품 (Product to review)</div>
                          <div className="project-preview-modal__info-value">{v.product?.name || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏷️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 가격 (Price)</div>
                          <div className="project-preview-modal__info-value">{Number(v.product?.price||0).toLocaleString()}원</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🔗</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제품 관련 (구매)참고 URL (Purchase URL)</div>
                          <div className="project-preview-modal__info-value" style={{ color: '#3b82f6', wordBreak: 'break-all' }}>{v.product?.purchasePlace || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {v?.serviceType === 'visit' && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🏛️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">장소/서비스 명 (Place/Service name)</div>
                          <div className="project-preview-modal__info-value">{v.product?.placeName || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">🗺️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">주소 or URL (Address or URL)</div>
                          <div className="project-preview-modal__info-value">{v.product?.placeAddress || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                  {(v?.serviceType === 'free' || v?.serviceType === 'custom') && (
                    <>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">📝</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">제목 (Title)</div>
                          <div className="project-preview-modal__info-value">{v.product?.customTitle || '-'}</div>
                        </div>
                      </div>
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">ℹ️</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">참고 설명 (Description)</div>
                          <div className="project-preview-modal__info-value">{v.product?.customDesc || '-'}</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                </div>

                {/* 미션 설명 및 가이드 */}
                <div className="project-preview-modal__card">
                  <div className="project-preview-modal__card-title project-preview-modal__card-title--large">⚠️ 광고주 요청 사항 (중요) (Advertiser Requirements — important)</div>
                  <div className="project-preview-modal__info-box-blue">
                    <div className="project-preview-modal__info-box-title">💡 선정된 인플루언서가 수행할 활동입니다 (Tasks for selected influencers)</div>
                    <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.5 }}>
                      아래 광고주 요청사항에 맞춰 콘텐츠를 제작하고 업로드해주세요. (Create and upload content according to the requirements below.)
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '15px', 
                    lineHeight: 1.8, 
                    color: '#334155',
                    padding: '12px',
                    background: '#f8fafc',
                    borderRadius: '16px'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: linkifyHtml(v?.description) || '<span style="color:#9ca3af">상세 가이드 내용이 등록되지 않았습니다. (No detailed guide has been registered.)</span>' }} />
                  </div>
                </div>
              </div>

              {/* 오른쪽 컬럼: 리워드, 일정, 담당자 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                
                {/* 리워드 혜택 */}
                <div className="project-preview-modal__card">
                  <div className="project-preview-modal__card-title">💰 참여 리워드 (Rewards)</div>
                  <div className="project-preview-modal__reward-large">
                    <div style={{ fontSize: '14px', fontWeight: 700, opacity: 0.9 }}>기본 리워드 (Base reward)</div>
                    <div className="project-preview-modal__reward-amount">{Number(v?.rewardPointPerCreator||0).toLocaleString()} pt</div>
                    <div style={{ fontSize: '13px', opacity: 0.8 }}>콘텐츠 최종 승인 시 즉시 지급 (Paid upon final approval)</div>
                  </div>
                  {v?.perPurchaseReward?.use && (
                    <>
                      <div className="project-preview-modal__info-box-blue" style={{ background: '#f0fdf4', borderColor: '#dcfce7' }}>
                        <div className="project-preview-modal__info-box-title" style={{ color: '#166534' }}>📈 성과형 리워드 (CPS) (Per-sale reward)</div>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: '#15803d' }}>
                          구매 고객 1명당 (Per buyer) {Number(v.perPurchaseReward.rewardPerBuyer||0).toLocaleString()} pt
                          {v.perPurchaseReward.budgetTotal && v.perPurchaseReward.settlementCount && v.headcount && (
                            <span> / 회차당 최대 (Max per round) {Math.floor(Number(v.perPurchaseReward.budgetTotal) / Number(v.headcount) / Number(v.perPurchaseReward.settlementCount)).toLocaleString()} pt</span>
                          )}
                          {v.perPurchaseReward.settlementCount && (
                            <span> / 총 {v.perPurchaseReward.settlementCount}회 결산 (settlement rounds)</span>
                          )}
                        </div>
                      </div>
                      <div className="project-preview-modal__cps-info">
                        <div className="project-preview-modal__cps-info-title">💡 성과형 리워드란? (What is CPS?)</div>
                        <div className="project-preview-modal__cps-info-text">
                          계약 시, 광고주가 인플루언서에게 URL을 제공합니다. 해당 URL을 통해 고객이 제품을 구매하게 될 시, 인플루언서에게 광고비가 추가로 지급됩니다. (The advertiser provides a tracking URL — you earn extra rewards for each purchase made through it.)
                        </div>
                      </div>
                    </>
                  )}
                  <div style={{ textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                    <b>{Number(v?.headcount||0)}명</b> 모집중 (Recruiting)
                  </div>
                </div>

                {/* 모집 조건 */}
                <div className="project-preview-modal__card">
                  <div className="project-preview-modal__card-title">📋 모집 조건 (Recruitment Criteria)</div>
                  
                  {/* 모집하는 SNS 플랫폼 */}
                  {v?.platforms && v.platforms.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--mb-text-title)', marginBottom: 10 }}>
                        모집하는 SNS 채널 (Target SNS channels)
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {v.platforms.map((platform, idx) => {
                          const platformLabels = {
                            instagram: '인스타그램 (Instagram)',
                            youtube: '유튜브 (YouTube)',
                            blog: '블로그 (Blog)',
                            tiktok: '틱톡 (TikTok)'
                          };
                          const platformIcons = {
                            instagram: '/images/sns/instagram.svg',
                            youtube: '/images/sns/youtube.svg',
                            blog: '/images/sns/blog.svg',
                            tiktok: '/images/sns/tiktok.svg'
                          };
                          return (
                            <div 
                              key={idx} 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px',
                                padding: '10px 16px',
                                background: '#f8f9fc',
                                border: '1px solid var(--mb-border-color)',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'var(--mb-text-body)'
                              }}
                            >
                              <img 
                                src={platformIcons[platform]} 
                                alt={platform}
                                style={{ width: 18, height: 18, objectFit: 'contain' }}
                              />
                              <span>{platformLabels[platform] || platform}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* 콘텐츠 형식 */}
                  {v?.contentType && (
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--mb-text-title)', marginBottom: 10 }}>
                        광고 콘텐츠 형식 (Content format)
                      </div>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '10px 18px',
                        background: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#92400e'
                      }}>
                        {v.contentType === 'post' && '📝 글/사진 포스팅 (Text/Photo post)'}
                        {v.contentType === 'video' && '🎥 동영상 콘텐츠 (Video content)'}
                        {v.contentType === 'free' && '✨ 자율형식 (Flexible)'}
                      </div>
                    </div>
                  )}
                </div>

                {/* 프로젝트 일정 */}
                <div className="project-preview-modal__card">
                  <div className="project-preview-modal__card-title">📅 주요 일정 안내 (Key Dates)</div>
                  <div className="project-preview-modal__info-list">
                    <div className="project-preview-modal__info-item">
                      <div className="project-preview-modal__info-icon-sm">⏳</div>
                      <div className="project-preview-modal__info-text-box">
                        <div className="project-preview-modal__info-label">모집 및 선정 마감 (Recruitment & selection deadline)</div>
                        <div className="project-preview-modal__info-value">{toDateStr(v?.recruitEnd)} 까지 (until)</div>
                        <div className="project-preview-modal__info-subtext">선정 시 안내 문자가 발송됩니다. (You will be notified via SMS if selected.)</div>
                      </div>
                    </div>
                    <div className="project-preview-modal__info-item">
                      <div className="project-preview-modal__info-icon-sm">📤</div>
                      <div className="project-preview-modal__info-text-box">
                        <div className="project-preview-modal__info-label">콘텐츠 제출 마감 (Content submission deadline)</div>
                        <div className="project-preview-modal__info-value">{toDateStr(v?.submitEnd)} 까지 (until)</div>
                        <div className="project-preview-modal__info-subtext">반드시 기한 내에 업로드 해주세요. (Please upload within the deadline.)</div>
                      </div>
                    </div>
                    {v?.perPurchaseReward?.use && v?.settlementDates?.length > 0 && (
                      <div className="project-preview-modal__info-item">
                        <div className="project-preview-modal__info-icon-sm">💰</div>
                        <div className="project-preview-modal__info-text-box">
                          <div className="project-preview-modal__info-label">CPS 결산일 (CPS settlement dates)</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {v.settlementDates.map((date, idx) => (
                              <div key={idx} className="project-preview-modal__info-value" style={{ fontSize: '14px' }}>
                                {idx + 1}회차 (Round {idx + 1}): {toDateStr(date)}
                              </div>
                            ))}
                          </div>
                          <div className="project-preview-modal__info-subtext">결산일에 성과형 리워드가 정산됩니다. (Per-sale rewards settle on these dates.)</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 담당자 정보 - 선정 시 공개 */}
                <div className="project-preview-modal__card" style={{ background: '#f8fafc' }}>
                  <div className="project-preview-modal__card-title">📞 프로젝트 지원 센터 (Project Support)</div>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '32px 20px',
                    color: '#64748b',
                    fontSize: '15px',
                    lineHeight: 1.6
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '12px' }}>🔒</div>
                    <div style={{ fontWeight: 600, color: '#475569' }}>
                      프로젝트에 선정되면<br />담당자 연락처가 공개됩니다
                      <span style={{ display: 'block', fontWeight: 500, color: '#94a3b8', marginTop: 4 }}>(Contact details are revealed once you are selected)</span>
                    </div>
                  </div>
                </div>

            </div>
          </div>
        </div>

        {/* Footer - 하단 고정 지원하기 버튼 */}
        <div className="project-preview-modal__footer">
          <button
            onClick={() => !v?.isApplied && setShowApplyModal(true)}
            disabled={v?.isApplied}
            className={`project-preview-modal__apply-btn ${v?.isApplied ? 'project-preview-modal__apply-btn--disabled' : ''}`}
          >
            {v?.isApplied
              ? (v?.applicationStatus === 'expired' ? '계약 만료 (Expired)' : '✓ 지원 완료 (Applied)')
              : '프로젝트 지원하기 (Apply to This Project)'}
          </button>
        </div>
      </div>
    </div>

      <ProjectApplyModal
        open={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        project={v}
        onSuccess={handleApplySuccess}
      />

      <ProjectApplyConfirmModal
        open={showConfirmModal}
        onClose={handleConfirmClose}
      />
    </>
  );
}
